import userService from '../user.service.js';

import { useTranslation } from 'react-i18next';
import useStore, { getCaregivers, getCareRecipients, updateScreenWidth, checkAndUpdateTimezone } from '../Store/Store';

import AppleIcon from '@mui/icons-material/Apple';
import FacebookIcon from '@mui/icons-material/Facebook';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Divider, Grid, IconButton, InputAdornment, Link, Typography } from '@mui/material';
import React from 'react';
import Logo from '../images/logo.png';
import SideImage from '../images/loginImage.svg';
import { loginApi } from '../utils/api';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { auth, provider, signInWithPopup, signInWithEmailAndPassword } from '../utils/firebase';
import { isEmpty } from 'lodash';
import Button from '../components/Button';
import TextField from '../components/TextInput';
import SocialLoginButton from '../components/SocialLoginButton';
import { requestNotificationPermission } from '../utils/firebaseNotificationConfigs.js';
import GoogleIcon from '../images/google.png';
import { OAuthProvider } from 'firebase/auth';
import LanguageSelector from '../components/LanguageSelector';
import { signCustomToken } from '../utils/firebase';
const LogIn = () => {
  const { t } = useTranslation();
  const history = useNavigate();
  const user = useStore((state) => state.user);
  const width = useStore((state) => state.screenWidth);
  const [state, setState] = React.useState({
    email: '',
    password: '',
    fcmToken: '',
    rememberMe: true,
    errorMessage: '',
    showPassword: false,
  });

  const updateDimensions = () => {
    updateScreenWidth(window.innerWidth);
  };
  React.useEffect(() => {
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const loginHandler = useMutation(loginApi, {
    onSuccess: async ({ data }) => {
      await Promise.all([
        getCareRecipients({ userId: data?.userId, token: data.token }),
        getCaregivers({ userId: data?.userId, token: data.token }),
      ]);

      userService.updateAuthData({
        accessToken: data.token,
        currentUserId: data.userId,
        rememberMe: state.rememberMe,
      });

      userService.updateStorage({
        accessToken: data.token,
        currentUserId: data.userId,
        rememberMe: state.rememberMe,
      });

      // Don't update user yet - wait until after Firebase sign-in to prevent early navigation
      // useStore.getState().updateUser(data?.user);
      userService.updateCurrentUser(data.user);
      localStorage.setItem('auth', data.token + '~29#215@rfeqwEt0');
      localStorage.setItem('refreshToken', data.refreshToken);
      sessionStorage.setItem('token', data.token);
      sessionStorage.setItem('refreshToken', data.refreshToken);
      sessionStorage.setItem('fromLogin', 'true');
      await checkAndUpdateTimezone();

      if (!data.firebaseCustomToken) {
        // Update user state before navigating
        useStore.getState().updateUser(data?.user);
        history('/main');
        return;
      }

      try {
        const response = await signCustomToken(data.firebaseCustomToken);

        if (response?.user?.uid) {
          // Now update user state - this will trigger the useEffect navigation
          useStore.getState().updateUser(data?.user);
          history('/main');
        } else {
          // Update user state even on error
          useStore.getState().updateUser(data?.user);
          history('/main');
        }
      } catch (error) {
        // Update user state even on error
        useStore.getState().updateUser(data?.user);
        // Still navigate to main even if Firebase sign-in fails
        history('/main');
      }
    },
    onError: (error) => {
      console.log('error =====> ', error);
      setState((prev) => {
        return { ...prev, errorMessage: error?.response?.data?.message || 'Something went wrong!' };
      });
    },
  });

  const login = async () => {
    if (!state.email || !state.password) {
      return setState((prev) => {
        return { ...prev, errorMessage: 'Email and Password are required fields' };
      });
    }

    setState((prev) => {
      return { ...prev, errorMessage: '' };
    });

    loginHandler.mutate({ email: state.email, password: state.password, fcmToken: state.fcmToken });
  };
  const handleAppleSignin = async () => {
    try {
      const provider = new OAuthProvider('apple.com');
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const idToken = await user.getIdToken();
      const { data } = await axios.post(
        process.env.REACT_APP_EXPRESS_URL + `/api/canCare/socialSignin`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            authorization: idToken,
          },
        },
      );
      const loginData = data?.data || null;
      console.log('🍎 Apple Login Response:', loginData);

      if (loginData?.user) {
        userService.updateAuthData({
          accessToken: loginData.token,
          currentUserId: loginData.userId,
          rememberMe: state.rememberMe,
        });
        userService.updateStorage({
          accessToken: loginData.token,
          currentUserId: loginData.userId,
          rememberMe: state.rememberMe,
        });
        useStore.getState().updateUser(loginData?.user);
        userService.updateCurrentUser(loginData.user);
        localStorage.setItem('auth', loginData.token + '~29#215@rfeqwEt0');

        // Save refresh token if available
        if (loginData.refreshToken) {
          localStorage.setItem('refreshToken', loginData.refreshToken);
          sessionStorage.setItem('refreshToken', loginData.refreshToken);
          console.log('✅ Apple: Refresh token saved');
        } else {
          console.log('⚠️ Apple: No refresh token in response');
        }

        sessionStorage.setItem('fromLogin', 'true');
        await checkAndUpdateTimezone();

        if (loginData.firebaseCustomToken) {
          try {
            const response = await signCustomToken(loginData.firebaseCustomToken);
            if (response?.user?.uid) {
              console.log('✅ Apple: Firebase sign-in successful, UID:', response.user.uid);
            }
          } catch (error) {
            console.error('⚠️ Apple: Firebase sign-in error:', error);
          }
        } else {
          console.log('⚠️ Apple: No Firebase token provided');
        }

        if (!loginData.user.careUserType) {
          history('/sign-up?step=1');
        } else {
          history('/main');
        }
      }
    } catch (error) {
      console.error('Error signing in with Apple:', error);
      setState((prev) => {
        return { ...prev, errorMessage: error?.response?.data?.message || 'Something went wrong!' };
      });
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const idToken = await user.getIdToken();

      const { data } = await axios.post(
        process.env.REACT_APP_EXPRESS_URL + `/api/canCare/socialSignin`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            authorization: idToken,
          },
        },
      );
      const loginData = data?.data || null;
      console.log('🔍 Google Login Response:', loginData);

      if (loginData?.user) {
        userService.updateAuthData({
          accessToken: loginData.token,
          currentUserId: loginData.userId,
          rememberMe: state.rememberMe,
        });

        userService.updateStorage({
          accessToken: loginData.token,
          currentUserId: loginData.userId,
          rememberMe: state.rememberMe,
        });

        useStore.getState().updateUser(loginData?.user);
        userService.updateCurrentUser(loginData.user);
        localStorage.setItem('auth', loginData.token + '~29#215@rfeqwEt0');
        sessionStorage.setItem('token', loginData.token);

        // Save refresh token if available
        if (loginData.refreshToken) {
          localStorage.setItem('refreshToken', loginData.refreshToken);
          sessionStorage.setItem('refreshToken', loginData.refreshToken);
          console.log('✅ Google: Refresh token saved');
        } else {
          console.log('⚠️ Google: No refresh token in response');
        }

        sessionStorage.setItem('fromLogin', 'true');
        await checkAndUpdateTimezone();

        if (loginData.firebaseCustomToken) {
          try {
            const response = await signCustomToken(loginData.firebaseCustomToken);
            if (response?.user?.uid) {
              console.log('✅ Google: Firebase sign-in successful, UID:', response.user.uid);
            }
          } catch (error) {
            console.error('⚠️ Google: Firebase sign-in error:', error);
          }
        } else {
          console.log('⚠️ Google: No Firebase token provided');
        }

        if (!loginData.user.careUserType) {
          history('/sign-up?step=1');
        } else {
          history('/main');
        }
      }
    } catch (error) {
      console.error('Error signing in with Google:', error);
      setState((prev) => {
        return { ...prev, errorMessage: error?.response?.data?.message || 'Something went wrong!' };
      });
    }
  };

  React.useEffect(() => {
    if (!isEmpty(user)) {
      history('/main');
    } else {
      console.log('useEffect: Not redirecting because user is empty');
    }
  }, [user]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      login();
    }
  };

  React.useEffect(() => {
    requestNotificationPermission().then((token) => {
      setState((prev) => {
        return { ...prev, fcmToken: token };
      });
      localStorage.setItem('fcmToken', token);
    });
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100vw',
        height: '100vh',
        backgroundColor: 'white',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: width > 1050 ? '50%' : '100%',
          padding: '1rem',
          position: 'relative',
        }}
      >
        {/* Language Selector positioned in top-right */}
        <Box
          sx={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            zIndex: 10,
          }}
        >
          <LanguageSelector variant="button" />
        </Box>

        <Box
          component="img"
          sx={{ width: '55px', height: '37.56px', marginBottom: '2rem' }}
          alt="Mask group"
          src={Logo}
        />
        <div style={{ paddingInline: '4rem', paddingBlock: '10rem' }}>
          <Typography variant="h4" gutterBottom mb={6}>
            {t('welcomeBack')}
          </Typography>

          <Grid container spacing={2} sx={{ marginBottom: '1rem', justifyContent: 'center' }}>
            <Grid item xs={3}>
              <SocialLoginButton
                text={t('apple')}
                icon={<AppleIcon />}
                onClick={handleAppleSignin}
                sx={{ fontSize: width > 1420 ? '14px' : '11px' }}
              />
            </Grid>
            <Grid item xs={3}>
              <SocialLoginButton
                text={t('google')}
                icon={<img src={GoogleIcon} style={{ width: '20px', height: '20px' }} alt="Google" />}
                onClick={handleGoogleSignIn}
                sx={{ fontSize: width > 1420 ? '14px' : '11px' }}
              />
            </Grid>
            {/* <Grid item xs={3}>
                <Button
                  variant="outlined"
                  startIcon={width > 1420 ? <MicrosoftIcon /> : null}
                  fullWidth
                  sx={{
                    borderRadius: '50px',
                    color: 'black',
                    border: '1px solid lightgrey',
                    fontSize: width > 1420 ? '14px' : '11px',
                  }}
                >
                  {width < 1420 ? <MicrosoftIcon /> : 'Microsoft'}
                </Button>
              </Grid>
              <Grid item xs={3}>
                <Button
                  variant="outlined"
                  startIcon={width > 1420 ? <FacebookIcon /> : null}
                  fullWidth
                  sx={{
                    borderRadius: '50px',
                    color: 'black',
                    border: '1px solid lightgrey',
                    fontSize: width > 1420 ? '14px' : '11px',
                  }}
                >
                  {width < 1420 ? <FacebookIcon /> : 'Facebook'}
                </Button>
              </Grid> */}
          </Grid>

          <Divider sx={{ marginY: '1rem', color: 'grey' }}>or</Divider>

          <TextField
            label={t('email')}
            fullWidth
            onKeyPress={handleKeyPress}
            onChange={(event) =>
              setState((prev) => {
                return { ...prev, email: event.target.value };
              })
            }
          />

          <TextField
            label={t('password')}
            fullWidth
            type={state.showPassword ? 'text' : 'password'}
            onKeyPress={handleKeyPress}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  onClick={() =>
                    setState((prev) => {
                      return { ...prev, showPassword: !state.showPassword };
                    })
                  }
                >
                  {!state.showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </IconButton>
              </InputAdornment>
            }
            onChange={(event) =>
              setState((prev) => {
                return { ...prev, password: event.target.value };
              })
            }
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
            }}
          >
            <div style={{ width: '100%', display: 'flex', justifyContent: 'end' }}>
              <Link href="/#/forgot-password" variant="body2" style={{ color: '#01796b', textDecoration: 'none' }}>
                {t('forgotPassword')}
              </Link>
            </div>
          </Box>

          <Button fullWidth text={t('logIn')} loading={loginHandler.isLoading} onClick={login} />

          <Typography component="h1" variant="subtitle1" style={{ color: 'red' }} textAlign="center" my={1}>
            {state.errorMessage}
          </Typography>

          <Typography variant="body2">
            {t('dontHaveAccount')}{' '}
            <Link
              variant="body2"
              style={{ color: '#01796b', textDecoration: 'none', cursor: 'pointer' }}
              href="/#/sign-up"
            >
              {t('signUp')}
            </Link>
          </Typography>
        </div>
      </Box>
      {width > 1050 && (
        <Box
          component="img"
          sx={{
            width: '50%',
            height: '97%',
            objectFit: 'cover',
            padding: '1rem',
            borderRadius: '30px',
          }}
          alt="Rectangle"
          src={SideImage}
        />
      )}
    </Box>
  );
};

export default LogIn;
